import { useRef, useState } from 'react';
import type { GridApi } from '@ag-grid-community/core';
import { RotateCcw, Download } from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import Container from '@/components/container';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGrid } from '@/contexts/grid-context';
import {
  useEmailTemplates,
  useCreateEmailTemplate,
  useUpdateEmailTemplate,
  useDeleteEmailTemplates,

} from '@/hooks/use-email-templates';
import type { EmailTemplate } from '@/hooks/use-email-templates';
import { templateColumnDefs } from './components/template-grid-columns';
import TemplateSheet from './components/template-sheet';

function TemplatesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState<EmailTemplate[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const gridApiRef = useRef<GridApi<EmailTemplate> | null>(null);
  const { registerGridApi } = useGrid();

  const { data: emailTemplates, isLoading } = useEmailTemplates();
  const createMutation = useCreateEmailTemplate();
  const updateMutation = useUpdateEmailTemplate();
  const deleteMutation = useDeleteEmailTemplates();

  const handleCreateSubmit = async (data: {
    name: string;
    short_description: string;
    long_description: string;
    tld: string;
    template: { body: string };
    to?: string;
    cc?: string;
    subject: string;
  }) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success('Template created successfully');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
      throw error;
    }
  };

  const handleUpdateSubmit = async (data: {
    name: string;
    short_description: string;
    long_description: string;
    tld: string;
    template: { body: string };
    to?: string;
    cc?: string;
    subject: string;
  }) => {
    if (!editingTemplate)
      return;

    try {
      await updateMutation.mutateAsync({
        oldName: editingTemplate.name,
        data,
      });
      toast.success('Template updated successfully');
      setEditingTemplate(null);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
      throw error;
    }
  };

  const handleDelete = async () => {
    if (selectedRows.length === 0)
      return;

    try {
      await deleteMutation.mutateAsync(selectedRows.map((row) => row.name));
      toast.success('Selected templates deleted successfully');
      setSelectedRows([]);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const handleRowDoubleClick = (event: { data: EmailTemplate | undefined }) => {
    if (!event.data)
      return;
    setEditingTemplate(event.data);
    setIsSheetOpen(true);
  };

  const handleSelectionChanged = (event: { api: { getSelectedRows: () => EmailTemplate[] } }) => {
    setSelectedRows(event.api.getSelectedRows());
  };

  const handleSheetOpenChange = (open: boolean) => {
    setIsSheetOpen(open);
    if (!open) {
      setEditingTemplate(null);
      // Deselect rows when closing the sheet
      if (gridApiRef.current) {
        gridApiRef.current.deselectAll();
      }
    }
  };

  const handleGridReady = (params: { api: GridApi<EmailTemplate> }) => {
    gridApiRef.current = params.api;
    registerGridApi(params.api);
  };

  const clearFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null);
      setSearchQuery('');
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  const handleExportSelected = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (selectedRows.length === 0) {
      toast.error('No templates selected for export');
      return;
    }

    try {
      // Create workbook and worksheet
      const wb = utils.book_new();
      const ws = utils.json_to_sheet(selectedRows.map((template) => ({
        Name: template.name,
        'Short Description': template.short_description,
        'Long Description': template.long_description,
        TLD: template.tld,
        To: template.to || '',
        Cc: template.cc || '',
        Subject: template.subject,
        'Created At': new Date(template.created_at).toLocaleDateString(),
        'Updated At': new Date(template.updated_at).toLocaleDateString(),
        'Created By': template.created_by,
        'Updated By': template.updated_by,
        'Template Body': template.template.body,
      })));

      // Auto-size columns
      ws['!cols'] = [
        { wch: 20 }, // Name
        { wch: 30 }, // Short Description
        { wch: 40 }, // Long Description
        { wch: 10 }, // TLD
        { wch: 30 }, // To
        { wch: 30 }, // Cc
        { wch: 40 }, // Subject
        { wch: 15 }, // Created At
        { wch: 15 }, // Updated At
        { wch: 20 }, // Created By
        { wch: 20 }, // Updated By
        { wch: 50 }, // Template Body
      ];

      // Add the worksheet to the workbook
      utils.book_append_sheet(wb, ws, 'Email Templates');

      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      const filename = `email_templates_export_${date}.xlsx`;

      // Write the file
      writeFile(wb, filename);

      toast.success(`Successfully exported ${selectedRows.length} template(s)`);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const handleCloneClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      for (const template of selectedRows) {
        const cloneData = {
          name: `${template.name}-copy`,
          short_description: template.short_description,
          long_description: template.long_description,
          tld: template.tld,
          template: template.template,
          to: template.to,
          cc: template.cc,
          subject: template.subject,
        };

        await createMutation.mutateAsync(cloneData);
      }

      toast.success(`Successfully cloned ${selectedRows.length} template(s)`);
      setSelectedRows([]);

      // Deselect rows after cloning
      if (gridApiRef.current) {
        gridApiRef.current.deselectAll();
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  return (
    <Container maxWidth={false} className="pb-12">
      <div className="mb-3 flex justify-between">
        <div>
          <h1 className="mt-2 text-xl font-bold">Email Templates</h1>
          <p className="text-sm text-muted-foreground">
            Create and manage email templates. Use
            {' '}
            {'{{'}
            {' '}
            and
            {' '}
            {'}}'}
            {' '}
            syntax for template variables (e.g.
            {' '}
            {'{{name}}'}
            ).
          </p>
        </div>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          {selectedRows.length > 0 && (
            <div className="flex flex-col gap-2 lg:flex-row">
              <Button
                variant="destructive"
                onClick={handleDeleteClick}
                disabled={deleteMutation.isPending}
                className="w-full sm:w-auto"
              >
                Delete Selected
              </Button>
              <Button
                variant="outline"
                onClick={handleCloneClick}
                disabled={createMutation.isPending}
                className="w-full sm:w-auto"
              >
                Clone Selected
              </Button>
              <Button
                variant="outline"
                onClick={handleExportSelected}
                className="w-full gap-2 sm:w-auto"
              >
                <Download className="size-4" />
                Export Selected
              </Button>
            </div>
          )}
          <Button
            onClick={() => setIsSheetOpen(true)}
            className="w-full sm:w-auto"
          >
            Create Template
          </Button>
        </div>
      </div>
      <div className="mb-6 flex gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          onClick={clearFilters}
          className="gap-2"
        >
          <RotateCcw className="size-4" />
          Clear Filters
        </Button>
      </div>
      <div className={isLoading ? 'opacity-50' : ''}>
        <AGDataGrid
          columnDefs={templateColumnDefs}
          rowData={emailTemplates || []}
          quickFilterText={searchQuery}
          rowSelection={{ mode: 'multiRow' }}
          onRowDoubleClicked={handleRowDoubleClick}
          onSelectionChanged={handleSelectionChanged}
          onGridReady={handleGridReady}
          overlayLoadingTemplate={
            isLoading ? '<span class="ag-overlay-loading-center">Loading...</span>' : undefined
          }
          overlayNoRowsTemplate={
            isLoading ? '' : '<span class="ag-overlay-no-rows-center">No templates found</span>'
          }
        />
      </div>

      <TemplateSheet
        open={isSheetOpen}
        onOpenChange={handleSheetOpenChange}
        initialData={editingTemplate || undefined}
        onSubmit={editingTemplate ? handleUpdateSubmit : handleCreateSubmit}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete
              {' '}
              {selectedRows.length}
              {' '}
              selected
              {' '}
              {selectedRows.length === 1 ? 'template' : 'templates'}
              .
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Container>
  );
}

export default TemplatesPage;
